// admin.onlytrade.ai/calculators — Phase 1a registry UI.
// Lets master_admin/super_admin/onlytrade_admin toggle each of the 120 calcs
// on/off, override their display name/description, leave an internal note,
// and bulk-toggle a whole trade. The actual formula/field definitions still
// live in the consumer app's calculators-data.js — this page only governs
// visibility + display copy. Formula editing arrives in Phase 2 (per-trade).
//
// Hits /onlytrade-api/admin/calculators (nginx proxies to OnlyTrade :3004).
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  calculators as calcApi,
  type AdminCalculator,
  type CalcTradeSummary,
} from '../api';

// Friendly trade labels — mirrors the keys the OnlyTrade backend recognises.
const TRADE_LABELS: Record<string, string> = {
  plumber: '🔧 Plumber',
  heating: '🌡️ Heating Eng.',
  electrician: '⚡ Electrician',
  gas: '🔥 Gas Engineer',
  lpg: '🛢️ LPG',
  roofer: '🏗️ Roofer',
  builder: '🧱 Builder',
  tiler: '⬜ Tiler',
  carpenter: '🔨 Carpenter',
  plasterer: '🎨 Plasterer',
  painter: '🖌️ Painter',
  landscaper: '🌿 Landscaper',
  bathroom: '🚿 Bathroom',
  kitchen: '🍳 Kitchen',
  drainage: '🚰 Drainage',
  universal: '🔧 Universal',
};

export default function CalculatorsPage() {
  const [calcs, setCalcs] = useState<AdminCalculator[] | null>(null);
  const [summary, setSummary] = useState<CalcTradeSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tradeFilter, setTradeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<AdminCalculator | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savingBulk, setSavingBulk] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await calcApi.list();
      setCalcs(d.calculators);
      setSummary(d.summary);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    if (!calcs) return [];
    const q = search.trim().toLowerCase();
    return calcs.filter(c => {
      // Trade filter matches primary OR any secondary — same semantics as
      // the consumer trade tab so admin sees the exact set LPG users see.
      if (tradeFilter !== 'all') {
        const inPrimary = c.trade_key === tradeFilter;
        const inSecondary = Array.isArray(c.secondary_trades) && c.secondary_trades.includes(tradeFilter);
        if (!inPrimary && !inSecondary) return false;
      }
      if (statusFilter === 'active' && !c.is_active) return false;
      if (statusFilter === 'inactive' && c.is_active) return false;
      if (q && !c.calc_id.toLowerCase().includes(q) && !(c.display_name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [calcs, tradeFilter, statusFilter, search]);

  // Totals reflect *distinct* calcs (one row per calc_id), not the sum of
  // chip counts — multi-trade calcs would double-count there.
  const totals = useMemo(() => {
    if (!calcs) return { total: 0, active: 0, curated: 0 };
    return {
      total: calcs.length,
      active: calcs.filter(c => c.is_active).length,
      curated: calcs.filter(c => c.is_curated).length,
    };
  }, [calcs]);

  // Recompute the per-trade summary from local state so toggles don't need a
  // round-trip to refresh the chips. Each calc counts against its primary
  // trade AND each secondary trade — mirrors the SQL summary so the chip
  // numbers match what consumer-side trade tabs render. A calc tagged
  // primary=gas, secondary=[lpg] adds 1 to Gas Engineer and 1 to LPG.
  function recalcSummary(list: AdminCalculator[]): CalcTradeSummary[] {
    const buckets = new Map<string, CalcTradeSummary>();
    function bump(tk: string, c: AdminCalculator) {
      const s = buckets.get(tk) || { trade_key: tk, total: 0, active: 0, curated: 0 };
      s.total++;
      if (c.is_active) s.active++;
      if (c.is_curated) s.curated++;
      buckets.set(tk, s);
    }
    for (const c of list) {
      bump(c.trade_key, c);
      if (Array.isArray(c.secondary_trades)) {
        for (const t of c.secondary_trades) bump(t, c);
      }
    }
    return [...buckets.values()].sort((a, b) => a.trade_key.localeCompare(b.trade_key));
  }

  // Optimistic update — flip the row's is_active immediately, rollback on error.
  // Single PATCH; we update local state from the server's RETURNING payload
  // rather than re-fetching the full list, so rapid toggling doesn't pile up
  // round-trips and trip rate limits.
  async function toggle(calc: AdminCalculator, next: boolean) {
    setSavingId(calc.calc_id);
    setCalcs(prev => {
      const updated = prev?.map(c => c.calc_id === calc.calc_id ? { ...c, is_active: next } : c) ?? null;
      if (updated) setSummary(recalcSummary(updated));
      return updated;
    });
    try {
      const fresh = await calcApi.update(calc.calc_id, { is_active: next });
      setCalcs(prev => {
        const updated = prev?.map(c => c.calc_id === fresh.calc_id ? fresh : c) ?? null;
        if (updated) setSummary(recalcSummary(updated));
        return updated;
      });
    } catch (e) {
      setError((e as Error).message);
      setCalcs(prev => {
        const reverted = prev?.map(c => c.calc_id === calc.calc_id ? { ...c, is_active: calc.is_active } : c) ?? null;
        if (reverted) setSummary(recalcSummary(reverted));
        return reverted;
      });
    } finally { setSavingId(null); }
  }

  async function saveEdit(updates: Partial<AdminCalculator>) {
    if (!editing) return;
    try {
      const fresh = await calcApi.update(editing.calc_id, updates);
      setEditing(null);
      // Splice the updated row in place — no full refetch.
      setCalcs(prev => {
        const updated = prev?.map(c => c.calc_id === fresh.calc_id ? fresh : c) ?? null;
        if (updated) setSummary(recalcSummary(updated));
        return updated;
      });
    } catch (e) { setError((e as Error).message); }
  }

  async function bulkToggle(trade_key: string, is_active: boolean) {
    const label = TRADE_LABELS[trade_key] || trade_key;
    // Bulk only affects calcs whose PRIMARY trade is this one — a multi-trade
    // calc (e.g. primary=gas, secondary=[lpg]) is left alone so flipping LPG
    // off doesn't accidentally hide it from Gas Engineer users too.
    if (!window.confirm(`Turn ${is_active ? 'ON' : 'OFF'} every calculator whose PRIMARY trade is ${label}?\n\nMulti-trade calcs (where ${label} is only a secondary tag) are unaffected — edit them individually if needed.`)) return;
    setSavingBulk(true);
    try {
      const r = await calcApi.bulk(trade_key, is_active);
      // Update local state directly from the returned calc_ids — no full
      // refetch, so a bulk action is just one round-trip total.
      const ids = new Set(r.calc_ids);
      setCalcs(prev => {
        const updated = prev?.map(c => ids.has(c.calc_id) ? { ...c, is_active } : c) ?? null;
        if (updated) setSummary(recalcSummary(updated));
        return updated;
      });
      window.alert(`${r.updated} calc(s) updated.`);
    } catch (e) { setError((e as Error).message); }
    finally { setSavingBulk(false); }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold">Calculators</h1>
          <p className="text-xs text-ink-muted mt-1">
            {totals.total} registered · {totals.active} active · {totals.curated} curated
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-950/50 border border-red-900 text-red-300 rounded p-3 text-sm mb-4">
          {error}
        </div>
      )}

      {/* Per-trade summary chips */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
        {summary.map(s => {
          const isActiveFilter = tradeFilter === s.trade_key;
          return (
            <button
              key={s.trade_key}
              onClick={() => setTradeFilter(isActiveFilter ? 'all' : s.trade_key)}
              className={`text-left p-2.5 rounded border transition-colors ${
                isActiveFilter
                  ? 'bg-brand-600/20 border-brand-600'
                  : 'bg-ink-card border-ink-border hover:border-brand-600/50'
              }`}
            >
              <div className="text-[11px] font-semibold tracking-wide">
                {TRADE_LABELS[s.trade_key] || s.trade_key}
              </div>
              <div className="text-xs text-ink-muted mt-0.5">
                {s.active}/{s.total} on
                {s.curated > 0 && <span className="ml-1 text-green-400">· {s.curated} ✓</span>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap gap-2 mb-3">
        <input
          type="text"
          placeholder="Search by calc id or name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] bg-ink-card border border-ink-border rounded px-3 py-2 text-sm"
        />
        <select
          value={tradeFilter}
          onChange={e => setTradeFilter(e.target.value)}
          className="bg-ink-card border border-ink-border rounded px-3 py-2 text-sm"
        >
          <option value="all">All trades</option>
          {summary.map(s => (
            <option key={s.trade_key} value={s.trade_key}>{TRADE_LABELS[s.trade_key] || s.trade_key}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
          className="bg-ink-card border border-ink-border rounded px-3 py-2 text-sm"
        >
          <option value="all">All status</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
        {tradeFilter !== 'all' && (
          <>
            <button
              disabled={savingBulk}
              onClick={() => bulkToggle(tradeFilter, false)}
              className="bg-red-900/40 hover:bg-red-900/60 border border-red-900 text-red-200 text-sm px-3 py-2 rounded disabled:opacity-50"
            >Turn ALL off</button>
            <button
              disabled={savingBulk}
              onClick={() => bulkToggle(tradeFilter, true)}
              className="bg-green-900/40 hover:bg-green-900/60 border border-green-900 text-green-200 text-sm px-3 py-2 rounded disabled:opacity-50"
            >Turn ALL on</button>
          </>
        )}
      </div>

      {/* Table */}
      {calcs === null && !error && (
        <div className="text-ink-muted text-sm">Loading calculators…</div>
      )}
      {calcs && calcs.length > 0 && (
        <div className="bg-ink-card border border-ink-border rounded overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-ink-border bg-ink-bg text-[11px] uppercase text-ink-muted font-semibold">
            <div className="col-span-1">On</div>
            <div className="col-span-3">Calc ID</div>
            <div className="col-span-3">Display name</div>
            <div className="col-span-2">Trade</div>
            <div className="col-span-2">Note</div>
            <div className="col-span-1 text-right">Edit</div>
          </div>
          {filtered.map(c => (
            <div key={c.calc_id}
                 className={`grid grid-cols-12 gap-2 px-3 py-2 border-b border-ink-border last:border-b-0 items-center ${
                   c.is_active ? '' : 'opacity-60'
                 } hover:bg-ink-bg/50`}>
              <div className="col-span-1">
                <button
                  disabled={savingId === c.calc_id}
                  onClick={() => toggle(c, !c.is_active)}
                  aria-label={c.is_active ? 'Turn off' : 'Turn on'}
                  className={`w-10 h-6 rounded-full relative transition-colors ${
                    c.is_active ? 'bg-green-600' : 'bg-ink-border'
                  } disabled:opacity-50`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                    c.is_active ? 'left-[18px]' : 'left-0.5'
                  }`} />
                </button>
              </div>
              <div className="col-span-3 text-xs font-mono text-ink-muted truncate" title={c.calc_id}>{c.calc_id}</div>
              <div className="col-span-3 text-sm truncate">
                {c.display_name || <span className="text-ink-muted italic">— code default —</span>}
                {c.is_curated && <span className="ml-1 text-green-400" title="Curated">✓</span>}
              </div>
              <div className="col-span-2 text-xs">
                <div>{TRADE_LABELS[c.trade_key] || c.trade_key}</div>
                {c.secondary_trades && c.secondary_trades.length > 0 && (
                  <div className="text-[10px] text-ink-muted mt-0.5 truncate" title={c.secondary_trades.join(', ')}>
                    + {c.secondary_trades.map(t => TRADE_LABELS[t] || t).join(', ')}
                  </div>
                )}
              </div>
              <div className="col-span-2 text-xs text-ink-muted truncate" title={c.admin_note || ''}>
                {c.admin_note || ''}
              </div>
              <div className="col-span-1 text-right">
                <button
                  onClick={() => setEditing(c)}
                  className="text-xs text-brand-400 hover:text-brand-300"
                >Edit</button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-ink-muted">No calculators match the filters.</div>
          )}
        </div>
      )}

      {/* Edit drawer */}
      {editing && (
        <EditDrawer
          calc={editing}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
        />
      )}
    </div>
  );
}

// ─── Edit drawer ─────────────────────────────────────────────────────────────
function EditDrawer({ calc, onClose, onSave }: {
  calc: AdminCalculator;
  onClose: () => void;
  onSave: (updates: Partial<AdminCalculator>) => Promise<void>;
}) {
  const [displayName, setDisplayName] = useState(calc.display_name ?? '');
  const [displayDesc, setDisplayDesc] = useState(calc.display_desc ?? '');
  const [helpText, setHelpText] = useState(calc.help_text ?? '');
  const [adminNote, setAdminNote] = useState(calc.admin_note ?? '');
  const [isCurated, setIsCurated] = useState(calc.is_curated);
  const [tradeKey, setTradeKey] = useState(calc.trade_key);
  const [secondaryTrades, setSecondaryTrades] = useState<string[]>(calc.secondary_trades ?? []);
  const [saving, setSaving] = useState(false);

  // Primary can't also be a secondary — strip if user picks the same trade.
  function toggleSecondary(key: string) {
    if (key === tradeKey) return;
    setSecondaryTrades(prev =>
      prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]
    );
  }

  function changePrimary(key: string) {
    setTradeKey(key);
    setSecondaryTrades(prev => prev.filter(t => t !== key));
  }

  async function submit() {
    setSaving(true);
    try {
      await onSave({
        display_name: displayName.trim() || null,
        display_desc: displayDesc.trim() || null,
        help_text: helpText.trim() || null,
        admin_note: adminNote.trim() || null,
        is_curated: isCurated,
        trade_key: tradeKey,
        secondary_trades: secondaryTrades,
      });
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-ink-border rounded-lg p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-ink-border">
          <div>
            <div className="text-xs text-ink-muted font-mono">{calc.calc_id}</div>
            <div className="text-base font-semibold">{calc.display_name || calc.calc_id}</div>
            <div className="text-xs text-ink-muted mt-1">
              {TRADE_LABELS[calc.trade_key] || calc.trade_key} · {calc.is_active ? 'Active' : 'Inactive'}
            </div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-text text-xl">×</button>
        </div>

        <label className="block text-xs uppercase text-ink-muted font-semibold tracking-wide mb-1">Primary trade</label>
        <select
          value={tradeKey}
          onChange={e => changePrimary(e.target.value)}
          className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2 text-sm mb-3"
        >
          {Object.entries(TRADE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        <label className="block text-xs uppercase text-ink-muted font-semibold tracking-wide mb-1">
          Also appears in <span className="text-ink-muted normal-case font-normal">(tap to toggle — for calcs that fit multiple trades)</span>
        </label>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {Object.entries(TRADE_LABELS).map(([key, label]) => {
            const isPrimary = key === tradeKey;
            const isOn = secondaryTrades.includes(key);
            return (
              <button
                key={key}
                type="button"
                disabled={isPrimary}
                onClick={() => toggleSecondary(key)}
                className={`text-xs px-2 py-1 rounded border transition-colors ${
                  isPrimary
                    ? 'bg-brand-600/20 border-brand-600 text-brand-300 cursor-not-allowed'
                    : isOn
                      ? 'bg-green-900/40 border-green-700 text-green-200'
                      : 'bg-ink-bg border-ink-border text-ink-muted hover:border-brand-600/50'
                }`}
                title={isPrimary ? 'Primary trade — change above to release this slot' : ''}
              >
                {label}{isPrimary ? ' (primary)' : ''}
              </button>
            );
          })}
        </div>

        <label className="block text-xs uppercase text-ink-muted font-semibold tracking-wide mb-1">Display name override</label>
        <input
          type="text"
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          placeholder="Leave blank to use the code default"
          className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2 text-sm mb-3"
        />

        <label className="block text-xs uppercase text-ink-muted font-semibold tracking-wide mb-1">Display description override</label>
        <textarea
          value={displayDesc}
          onChange={e => setDisplayDesc(e.target.value)}
          placeholder="Leave blank to use the code default"
          rows={2}
          className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2 text-sm mb-3"
        />

        <label className="block text-xs uppercase text-ink-muted font-semibold tracking-wide mb-1">
          Help text <span className="text-ink-muted normal-case font-normal">(rendered under the inputs — markdown: <code>**bold**</code>, <code>- bullets</code>, line breaks)</span>
        </label>
        <textarea
          value={helpText}
          onChange={e => setHelpText(e.target.value)}
          placeholder={'**Where do these numbers come from?**\n- Max velocity: 1.0 m/s hot, 1.5 m/s cold (BS 6700)\n- Flow rate: shower 9 L/min, basin 6 L/min, bath 18-24 L/min'}
          rows={6}
          className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2 text-sm font-mono mb-3"
        />

        <label className="block text-xs uppercase text-ink-muted font-semibold tracking-wide mb-1">Admin note (internal)</label>
        <textarea
          value={adminNote}
          onChange={e => setAdminNote(e.target.value)}
          placeholder='e.g. "turned off 2026-04-27 — Daniel reported double-counted VAT"'
          rows={2}
          className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2 text-sm mb-3"
        />

        <label className="flex items-center gap-2 mb-4 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={isCurated}
            onChange={e => setIsCurated(e.target.checked)}
            className="w-4 h-4"
          />
          <span>Mark as curated <span className="text-ink-muted text-xs">(audited & confirmed by us)</span></span>
        </label>

        <div className="text-[11px] text-ink-muted mb-3">
          Last edit: {calc.modified_at ? new Date(calc.modified_at).toLocaleString() : '—'}
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="text-sm px-3 py-2 border border-ink-border rounded hover:bg-ink-bg">Cancel</button>
          <button
            disabled={saving}
            onClick={submit}
            className="bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded"
          >{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
